import { useCallback, useEffect, useState } from "react";

import { listLocationTypes } from "../services/onboarding/locationTypes";
import { LocationTypeOption } from "../types/onboarding";

interface UseLocationTypes {
  options: LocationTypeOption[];
  loading: boolean;
  error: boolean;
  /**
   * Resolve a location type back to the identifier the API accepts.
   *
   * This exists because the field is asymmetric: a prospect response contains
   * the human-readable label ("Private household") while a write expects the
   * identifier ("PRIVATE_HOUSEHOLD") — and the backend silently discards a
   * value it cannot parse, which looks like the field clearing itself. So the
   * edit form must never echo back what it displayed.
   *
   * Accepts a label or an already-resolved identifier, and returns null when
   * neither matches.
   */
  valueForLabel: (label: string | null | undefined) => string | null;
}

/**
 * The supported location types, loaded from the backend so the list can never
 * drift from what the public onboarding form offers.
 *
 * `enabled` lets a dialog defer the fetch until it is actually opened.
 */
const useLocationTypes = (enabled = true): UseLocationTypes => {
  const [options, setOptions] = useState<LocationTypeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    listLocationTypes()
      .then((list) => {
        if (!cancelled) setOptions(list);
      })
      .catch((e) => {
        console.error("Failed to load location types", e);
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const valueForLabel = useCallback(
    (label: string | null | undefined): string | null => {
      if (!label) return null;
      const needle = label.trim().toLowerCase();
      const byLabel = options.find((o) => o.label?.trim().toLowerCase() === needle);
      if (byLabel) return byLabel.value;
      // Tolerate an identifier being passed in — harmless, and keeps the form
      // working if the API ever starts returning identifiers instead.
      const byValue = options.find((o) => o.value?.trim().toLowerCase() === needle);
      return byValue ? byValue.value : null;
    },
    [options]
  );

  return { options, loading, error, valueForLabel };
};

export default useLocationTypes;
